import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import type {
  AddCardToCardCommand,
  AddCardVoteCommand,
  ChangeColumnDescriptionCommand,
  ChangeColumnNameCommand,
  ChangeCurrentDiscussCardCommand,
  ChangeSlotMachineVisibilityCommand,
  ChangeTimerCommand,
  ChangeVoteAmountCommand,
  CompleteWarmupCommand,
  CreateCardCommand,
  CreateTaskCommand,
  DeleteCardCommand,
  DeleteTaskCommand,
  DrawMachineCommand,
  MoveCardToColumnCommand,
  RemoveCardVoteCommand,
  ReorderColumnsCommand,
  StartWarmupDrawCommand,
  UpdateCardCommand,
  UpdateCreatingTaskStateCommand,
  UpdateReadyStateCommand,
  UpdateRoomStateCommand,
  UpdateTaskCommand,
  UpdateWarmupRoomUrlCommand,
  UpdateWriteStateCommand,
} from "shared/model/retro/retro.commands";
import type {
  ColumnDescriptionChangedEvent,
  ColumnNameChangedEvent,
  ColumnsReorderedEvent,
  RoomState,
  RoomSyncEvent,
  SlotMachineDrawnEvent,
  TimerChangedEvent,
  WarmupDrawStartedEvent,
  WarmupRoomUrlUpdatedEvent,
} from "shared/model/retro/retro.events";
import type {
  Card,
  RetroColumn,
  RetroTask,
  User,
  Vote,
} from "shared/model/retro/retroRoom.interface";
import type { UserResponse } from "shared/model/user/user.response";
import type { WarmupState } from "shared/model/warmup/warmup";
import io, { type Socket } from "socket.io-client";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { UserService } from "@/api/User.service";
import { CardMoveAction } from "@/components/molecules/dragndrop/dragndrop";
import { SLOT_MACHINE_ANIMATION_DURATION } from "@/components/organisms/slot_machine/SlotMachine";
import { useUser } from "@/context/user/UserContext.hook";
import { groupCards } from "@/lib/groupCards";
import { usePreferencesStore } from "@/store/usePreferencesStore";

interface RetroContextParams {
  retroId: string;
}

export type SlotMachineDrawnListener = (event: {
  highlightedUserId: string;
  actorId: string;
}) => void;

interface RetroContext {
  retroId: string;
  teamId: string | null;
  columns: RetroColumn[];
  cards: Card[];
  teamUsers: UserResponse[];
  activeUsers: User[];

  // common
  roomState: RoomState;
  nextRoomState: () => void;
  prevRoomState: () => void;
  endRetro: () => void;

  timerEnds: number | null;
  setTimer: (time: number | null) => void;

  ready: boolean;
  setReady: (ready: boolean) => void;
  readyPercentage: number;

  // reflection
  setWriting: (value: boolean, columnId: string) => void;
  createCard: (text: string, columnId: string) => void;
  updateCard: (cardId: string, text: string) => void;
  deleteCard: (cardId: string) => void;
  changeColumnName: (columnId: string, name: string) => void;
  changeColumnDescription: (columnId: string, description: string) => void;
  reorderColumns: (fromColumnId: string, toColumnId: string) => void;

  // group
  slotMachineVisible: boolean;
  setSlotMachineVisible: (visible: boolean) => void;
  drawMachine: () => void;
  highlightedUserId: string | null;
  addDrawSlotMachineListener: (listener: SlotMachineDrawnListener) => void;
  removeDrawSlotMachineListener: (listener: SlotMachineDrawnListener) => void;
  moveCard: (action: CardMoveAction) => void;

  // vote
  removeVote: (parentCardId: string) => void;
  addVote: (parentCardId: string) => void;
  votes: Vote[];
  maxVotes: number;
  setMaxVotesAmount: (amount: number) => void;

  // discuss
  discussionCardId: string | null;
  setCreatingTask: (creatingTask: boolean) => void;
  createTask: (text: string, ownerId: string) => void;
  updateTask: (
    actionPointId: string,
    userId: string | null,
    text: string,
  ) => void;
  deleteTask: (actionPointId: string) => void;
  tasks: RetroTask[];

  warmup: WarmupState | null;
  startWarmupDraw: () => void;
  updateWarmupRoomUrl: (url: string) => void;
  completeWarmup: () => void;
}

export const RetroContext = createContext<RetroContext>({
  retroId: "",
  teamId: null,
  columns: [],
  cards: [],
  teamUsers: [],
  activeUsers: [],

  // common
  roomState: "reflection",
  nextRoomState: () => {},
  prevRoomState: () => {},
  endRetro: () => {},

  timerEnds: null,
  setTimer: () => {},

  ready: false,
  setReady: () => {},
  readyPercentage: 0,

  // reflection
  setWriting: () => {},
  createCard: () => {},
  updateCard: () => {},
  deleteCard: () => {},
  changeColumnName: () => {},
  changeColumnDescription: () => {},
  reorderColumns: () => {},

  // group
  slotMachineVisible: false,
  setSlotMachineVisible: () => {},
  drawMachine: () => {},
  highlightedUserId: null,
  addDrawSlotMachineListener: () => {},
  removeDrawSlotMachineListener: () => {},
  moveCard: () => {},

  // vote
  removeVote: () => {},
  addVote: () => {},
  votes: [],
  maxVotes: 0,
  setMaxVotesAmount: () => {},

  // discuss
  discussionCardId: null,
  setCreatingTask: () => {},
  createTask: () => {},
  updateTask: () => {},
  deleteTask: () => {},
  tasks: [],
  warmup: null,
  startWarmupDraw: () => {},
  updateWarmupRoomUrl: () => {},
  completeWarmup: () => {},
});

export const RetroContextProvider: React.FC<
  React.PropsWithChildren<RetroContextParams>
> = ({ children, retroId }) => {
  const { user } = useUser();
  const navigate = useNavigate();

  const timeOffset = useRef<number>(0);
  const socket = useRef<Socket>(undefined);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [columns, setColumns] = useState<RetroColumn[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [teamUsers, setTeamUsers] = useState<UserResponse[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [roomState, setRoomState] = useState<RoomState>("reflection");
  const [timerEnds, setTimerEnds] = useState<number | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [usersReady, setUsersReady] = useState<number>(0);
  const readyPercentage = (usersReady / users.length) * 100;

  const slotMachineDrawnListeners = useRef<SlotMachineDrawnListener[]>([]);
  const [isSlotMachineVisible, setIsSlotMachineVisible] =
    useState<boolean>(false);
  const [highlightedUserId, setHighlightedUserId] = useState<string | null>(
    null,
  );

  const [votes, setVotes] = useState<Vote[]>([]);
  const [maxVotes, setMaxVotes] = useState<number>(0);

  const [discussionCardId, setDiscussionCardId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<RetroTask[]>([]);
  const [warmup, setWarmup] = useState<WarmupState | null>(null);
  const [autoReadyDraw, setAutoReadyDraw] = useState(0);

  const setReady = useCallback((ready: boolean) => {
    const command: UpdateReadyStateCommand = {
      readyState: ready,
    };
    socket.current?.emit("command_ready", command);
    setIsReady(ready);
  }, []);

  const handleTimerChanged = useCallback(
    (time: number | null, serverOffset: number) => {
      const timerEnds = time ? time - serverOffset : null;
      const roundedTime = timerEnds ? Math.round(timerEnds / 100) * 100 : null;

      setTimerEnds(roundedTime);
    },
    [],
  );

  const fetchTeamUsers = useCallback(async (teamId: string) => {
    const users = await UserService.getUsersByTeamId(teamId);
    setTeamUsers(users);
  }, []);

  useEffect(() => {
    const createdSocket = io(`${process.env.RETRO_WEB_SOCKET_URL}/retro`, {
      query: {
        retro_id: retroId,
      },
      extraHeaders: {
        //@ts-expect-error
        Authorization: window.localStorage.getItem("Bearer"),
      },
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: Number.POSITIVE_INFINITY,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
      randomizationFactor: 0.5,
    });
    socket.current = createdSocket;

    createdSocket.on("error", (e) => {
      console.log(e);
      toast.error("Wystąpił błąd");
    });

    createdSocket.on("event_room_sync", (roomData: RoomSyncEvent) => {
      setRoomState(roomData.roomState);
      setTeamId(roomData.teamId);
      setColumns(roomData.retroColumns);
      setCards(roomData.cards);
      setUsersReady(roomData.usersReady);
      setVotes(roomData.votes);
      setMaxVotes(roomData.maxVotes);
      setIsReady(
        roomData.users.find((u) => u.userId === user?.id)?.isReady || false,
      );
      setIsSlotMachineVisible(roomData.slotMachineVisible);
      setHighlightedUserId(roomData.highlightedUserId);
      setUsers(roomData.users);
      setTasks(roomData.tasks);
      setDiscussionCardId(roomData.discussionCardId);
      setWarmup(roomData.warmup);

      const serverTimeOffset = roomData.serverTime - Date.now();
      timeOffset.current = serverTimeOffset;
      handleTimerChanged(roomData.timerEnds, serverTimeOffset);
    });

    createdSocket.on("event_timer_change", (e: TimerChangedEvent) => {
      handleTimerChanged(e.timerEnds, timeOffset.current ?? 0);
    });

    createdSocket.on(
      "event_warmup_draw_started",
      ({ resultId, spinEndsAt }: WarmupDrawStartedEvent) => {
        setWarmup((current) =>
          current
            ? {
                ...current,
                status: "spinning",
                spinEndsAt,
                result:
                  current.candidates.find((item) => item.id === resultId) ??
                  null,
                sharedRoomUrl: null,
              }
            : null,
        );
      },
    );

    createdSocket.on(
      "event_warmup_room_url_updated",
      (event: WarmupRoomUrlUpdatedEvent) => {
        setWarmup((current) =>
          current
            ? {
                ...current,
                sharedRoomUrl: event.url,
                sharedRoomUrlRevision: event.revision,
                sharedRoomUrlUpdatedBy: event.actorId,
              }
            : null,
        );
        if (event.actorId !== user?.id) {
          toast.info("Link do rozgrzewki został zaktualizowany");
        }
      },
    );

    createdSocket.on(
      "event_column_name_changed",
      ({ columnId, name }: ColumnNameChangedEvent) => {
        setColumns((current) =>
          current.map((column) =>
            column.id === columnId ? { ...column, name } : column,
          ),
        );
      },
    );

    createdSocket.on(
      "event_column_description_changed",
      ({ columnId, description }: ColumnDescriptionChangedEvent) => {
        setColumns((current) =>
          current.map((column) =>
            column.id === columnId ? { ...column, description } : column,
          ),
        );
      },
    );

    createdSocket.on(
      "event_columns_reordered",
      ({ columnIds }: ColumnsReorderedEvent) => {
        setColumns((current) => {
          if (
            columnIds.length !== current.length ||
            columnIds.some((id) => !current.some((column) => column.id === id))
          ) {
            return current;
          }
          return columnIds.flatMap((id) => {
            const column = current.find((item) => item.id === id);
            return column ? [column] : [];
          });
        });
      },
    );

    createdSocket.on(
      "event_slot_machine_drawn",
      (event: SlotMachineDrawnEvent) => {
        setHighlightedUserId(event.highlightedUserId);

        if (event.highlightedUserId === user?.id) {
          const { autoReadyAfterDraw } = usePreferencesStore.getState();
          if (autoReadyAfterDraw) {
            setAutoReadyDraw((draw) => draw + 1);
          }
        }

        for (const listener of slotMachineDrawnListeners.current) {
          listener({
            highlightedUserId: event.highlightedUserId,
            actorId: event.actorId,
          });
        }
      },
    );

    createdSocket.on("event_close_room", () => {
      navigate(`/retro/${retroId}/summary`);
    });

    return () => {
      createdSocket.off("error");
      createdSocket.off("event_room_sync");
      createdSocket.off("event_timer_change");
      createdSocket.off("event_warmup_draw_started");
      createdSocket.off("event_warmup_room_url_updated");
      createdSocket.off("event_column_name_changed");
      createdSocket.off("event_column_description_changed");
      createdSocket.off("event_columns_reordered");
      createdSocket.off("event_slot_machine_drawn");
      createdSocket.off("event_close_room");
      createdSocket.disconnect();
    };
  }, [retroId, user?.id, navigate, handleTimerChanged]);

  useEffect(() => {
    if (autoReadyDraw === 0) return;
    const timeout = setTimeout(() => {
      setReady(true);
    }, SLOT_MACHINE_ANIMATION_DURATION);
    return () => clearTimeout(timeout);
  }, [autoReadyDraw, setReady]);

  useEffect(() => {
    const listener = () => {
      if (!teamId) return;

      fetchTeamUsers(teamId);
    };

    const activeSocket = socket.current;
    activeSocket?.on("event_team_users_change", listener);

    return () => {
      activeSocket?.off("event_team_users_change", listener);
    };
  }, [teamId, fetchTeamUsers]);

  useEffect(() => {
    if (!teamId) return;

    fetchTeamUsers(teamId);
  }, [teamId, fetchTeamUsers]);

  // common

  const setTimer = useCallback((time: number | null) => {
    setTimerEnds(time);
    const command: ChangeTimerCommand = {
      timestamp: time ? time + (timeOffset.current ?? 0) : null,
    };
    socket.current?.emit("command_timer_change", command);
  }, []);

  const changeDiscussCard = useCallback(
    (to: "next" | "prev") => {
      const groups = groupCards(cards, votes).sort(
        (a, b) => b.votes.length - a.votes.length,
      );

      if (!discussionCardId) {
        return false;
      }
      const currentIndex = groups.findIndex(
        (g) => g.parentCardId === discussionCardId,
      );
      const targetIndex = to === "next" ? currentIndex + 1 : currentIndex - 1;

      if (to === "next" && targetIndex >= groups.length) {
        return true;
      }
      if (to === "prev" && targetIndex < 0) {
        return true;
      }

      const targetCardId = groups[targetIndex]?.parentCardId;

      const command: ChangeCurrentDiscussCardCommand = {
        cardId: targetCardId,
      };
      socket.current?.emit("command_change_discussion_card", command);

      return false;
    },
    [cards, votes, discussionCardId],
  );

  const nextRoomState = useCallback(() => {
    let state: RoomState;

    switch (roomState) {
      case "reflection":
        state = "group";
        break;
      case "group":
        state = "vote";
        break;
      case "vote":
        state = "discuss";
        break;
      case "discuss":
        changeDiscussCard("next");
        return;
      default:
        return;
    }

    const command: UpdateRoomStateCommand = {
      roomState: state,
    };

    socket.current?.emit("command_room_state", command);
  }, [roomState, changeDiscussCard]);

  const prevRoomState = useCallback(() => {
    let state: RoomState;
    switch (roomState) {
      case "reflection":
        if (!warmup) return;
        state = "warmup";
        break;
      case "group":
        state = "reflection";
        break;
      case "vote":
        state = "group";
        break;
      case "discuss":
        if (changeDiscussCard("prev")) {
          state = "vote";
          break;
        }

        return;
      default:
        return;
    }

    const command: UpdateRoomStateCommand = {
      roomState: state,
    };
    socket.current?.emit("command_room_state", command);
  }, [roomState, warmup, changeDiscussCard]);

  const endRetro = useCallback(() => {
    socket.current?.emit("command_close_room");
  }, []);

  const startWarmupDraw = useCallback(() => {
    const command: StartWarmupDrawCommand = {};
    socket.current?.emit("command_start_warmup_draw", command);
  }, []);

  const updateWarmupRoomUrl = useCallback((url: string) => {
    const command: UpdateWarmupRoomUrlCommand = { url };
    socket.current?.emit("command_update_warmup_room_url", command);
  }, []);

  const completeWarmup = useCallback(() => {
    const command: CompleteWarmupCommand = {};
    socket.current?.emit("command_complete_warmup", command);
  }, []);

  // reflection
  const setWriting = useCallback((value: boolean, columnId: string) => {
    const command: UpdateWriteStateCommand = {
      columnId: columnId,
      writeState: value,
    };
    socket.current?.emit("command_write_state", command);
  }, []);

  const createCard = useCallback(
    (text: string, columnId: string) => {
      const command: CreateCardCommand = {
        id: uuidv4(),
        text: text,
        columnId: columnId,
      };
      socket.current?.emit("command_create_card", command);
      setWriting(false, columnId);
    },
    [setWriting],
  );

  const updateCard = useCallback((cardId: string, text: string) => {
    const command: UpdateCardCommand = {
      cardId: cardId,
      text: text,
    };
    socket.current?.emit("command_update_card", command);
  }, []);

  const deleteCard = useCallback((cardId: string) => {
    const command: DeleteCardCommand = {
      cardId: cardId,
    };
    socket.current?.emit("command_delete_card", command);
  }, []);

  const changeColumnName = useCallback((columnId: string, name: string) => {
    const command: ChangeColumnNameCommand = { columnId, name };
    socket.current?.emit("command_change_column_name", command);
  }, []);

  const changeColumnDescription = useCallback(
    (columnId: string, description: string) => {
      const command: ChangeColumnDescriptionCommand = { columnId, description };
      socket.current?.emit("command_change_column_description", command);
    },
    [],
  );

  const reorderColumns = useCallback(
    (fromColumnId: string, toColumnId: string) => {
      const command: ReorderColumnsCommand = { fromColumnId, toColumnId };
      socket.current?.emit("command_reorder_columns", command);
    },
    [],
  );

  // group
  const drawMachine = useCallback(() => {
    const command: DrawMachineCommand = {};
    socket.current?.emit("command_draw_slot_machine", command);
  }, []);

  const setSlotMachineVisible = useCallback((visible: boolean) => {
    const command: ChangeSlotMachineVisibilityCommand = {
      isVisible: visible,
    };
    socket.current?.emit("command_change_slot_machine_visibility", command);
  }, []);

  const addDrawSlotMachineListener = useCallback(
    (listener: SlotMachineDrawnListener) => {
      slotMachineDrawnListeners.current.push(listener);
    },
    [],
  );

  const removeDrawSlotMachineListener = useCallback(
    (listener: SlotMachineDrawnListener) => {
      slotMachineDrawnListeners.current =
        slotMachineDrawnListeners.current.filter((l) => l !== listener);
    },
    [],
  );

  const moveCard = useCallback((move: CardMoveAction) => {
    if (move.targetType === "column") {
      const command: MoveCardToColumnCommand = {
        cardId: move.cardId,
        columnId: move.targetId,
      };
      socket.current?.emit("command_move_card_to_column", command);
    } else if (move.targetType === "card") {
      const command: AddCardToCardCommand = {
        cardId: move.cardId,
        parentCardId: move.targetId,
      };
      socket.current?.emit("command_card_add_to_card", command);
    }
  }, []);

  // vote
  const addVote = useCallback(
    (parentCardId: string) => {
      const command: AddCardVoteCommand = {
        parentCardId: parentCardId,
      };
      socket.current?.emit("command_vote_on_card", command);

      const allUserVotes = votes.filter((v) => v.voterId === user?.id);
      const isLastAvailableVote = allUserVotes.length === maxVotes - 1;

      const { autoReadyAfterVoting } = usePreferencesStore.getState();

      if (isLastAvailableVote && autoReadyAfterVoting) {
        setReady(true);
      }
    },
    [votes, user?.id, maxVotes, setReady],
  );

  const removeVote = useCallback((parentCardId: string) => {
    const command: RemoveCardVoteCommand = {
      parentCardId: parentCardId,
    };
    socket.current?.emit("command_remove_vote_on_card", command);
  }, []);

  const setMaxVotesAmount = useCallback((amount: number) => {
    const command: ChangeVoteAmountCommand = {
      votesAmount: amount,
    };
    socket.current?.emit("command_change_vote_amount", command);
  }, []);

  // discuss

  const setCreatingTask = useCallback((creatingTask: boolean) => {
    const command: UpdateCreatingTaskStateCommand = {
      creatingTaskState: creatingTask,
    };
    socket.current?.emit("command_creating_task_state", command);
  }, []);

  const createTask = useCallback(
    (text: string, ownerId: string) => {
      const command: CreateTaskCommand = {
        description: text,
        ownerId: ownerId,
      };
      socket.current?.emit("command_create_action_point", command);
      setCreatingTask(false);
    },
    [setCreatingTask],
  );

  const updateTask = useCallback(
    (taskId: string, userId: string | null, text: string) => {
      const command: UpdateTaskCommand = {
        taskId: taskId,
        ownerId: userId,
        description: text,
      };
      socket.current?.emit("command_update_action_point", command);
    },
    [],
  );

  const deleteTask = useCallback((taskId: string) => {
    const command: DeleteTaskCommand = {
      taskId: taskId,
    };

    socket.current?.emit("command_delete_action_point", command);
  }, []);

  const contextValue = useMemo(
    () => ({
      retroId: retroId,
      teamId: teamId,
      columns: columns,
      cards: cards,
      teamUsers: teamUsers,
      activeUsers: users,

      roomState: roomState,
      nextRoomState: nextRoomState,
      prevRoomState: prevRoomState,
      endRetro: endRetro,

      timerEnds: timerEnds,
      setTimer: setTimer,

      ready: isReady,
      setReady: setReady,
      readyPercentage: readyPercentage,

      // reflection
      setWriting: setWriting,
      createCard: createCard,
      updateCard: updateCard,
      deleteCard: deleteCard,
      changeColumnName: changeColumnName,
      changeColumnDescription: changeColumnDescription,
      reorderColumns: reorderColumns,

      // group
      slotMachineVisible: isSlotMachineVisible,
      setSlotMachineVisible: setSlotMachineVisible,
      drawMachine: drawMachine,
      highlightedUserId: highlightedUserId,
      addDrawSlotMachineListener: addDrawSlotMachineListener,
      removeDrawSlotMachineListener: removeDrawSlotMachineListener,
      moveCard: moveCard,

      // vote
      removeVote: removeVote,
      addVote: addVote,
      votes: votes,
      maxVotes: maxVotes,
      setMaxVotesAmount: setMaxVotesAmount,

      // discuss
      discussionCardId: discussionCardId,
      setCreatingTask: setCreatingTask,
      createTask: createTask,
      updateTask: updateTask,
      deleteTask: deleteTask,
      tasks: tasks,
      warmup,
      startWarmupDraw,
      updateWarmupRoomUrl,
      completeWarmup,
    }),
    [
      retroId,
      teamId,
      columns,
      cards,
      teamUsers,
      users,
      roomState,
      nextRoomState,
      prevRoomState,
      endRetro,
      timerEnds,
      setTimer,
      isReady,
      setReady,
      readyPercentage,
      setWriting,
      createCard,
      updateCard,
      deleteCard,
      changeColumnName,
      changeColumnDescription,
      reorderColumns,
      isSlotMachineVisible,
      setSlotMachineVisible,
      drawMachine,
      highlightedUserId,
      addDrawSlotMachineListener,
      removeDrawSlotMachineListener,
      moveCard,
      removeVote,
      addVote,
      votes,
      maxVotes,
      setMaxVotesAmount,
      discussionCardId,
      setCreatingTask,
      createTask,
      updateTask,
      deleteTask,
      tasks,
      warmup,
      startWarmupDraw,
      updateWarmupRoomUrl,
      completeWarmup,
    ],
  );

  return (
    <RetroContext.Provider value={contextValue}>
      {children}
    </RetroContext.Provider>
  );
};
